import { c as _c } from "react/compiler-runtime";
import type { StoreApi, UseBoundStore } from 'zustand';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
type ObjectLike = object;
type ZustandStore<TState extends ObjectLike> = StoreApi<TState> | UseBoundStore<StoreApi<TState>>;
type StringKeyOf<TState> = Extract<keyof TState, string>;
type LastPathSegment<TPath extends string> = TPath extends `${string}.${infer Rest}` ? LastPathSegment<Rest> : TPath;
type PathValue<TObject extends ObjectLike, TPath extends string> = TPath extends `${infer Head}.${infer Tail}` ? Head extends StringKeyOf<TObject> ? TObject[Head] extends ObjectLike ? PathValue<TObject[Head], Tail> : never : never : TPath extends StringKeyOf<TObject> ? TObject[TPath] : never;
type UnionToIntersection<TUnion> = (TUnion extends unknown ? (value: TUnion) => void : never) extends ((value: infer TIntersection) => void) ? TIntersection : never;
type Simplify<TValue> = { [TKey in keyof TValue]: TValue[TKey] } & {};
export type ZustandSliceKey<TState extends ObjectLike> = { [TKey in StringKeyOf<TState>]: TState[TKey] extends ObjectLike ? TKey | `${TKey}.${ZustandSliceKey<TState[TKey]>}` : TKey }[StringKeyOf<TState>];
type SliceFromKey<TState extends ObjectLike, TKey extends ZustandSliceKey<TState>> = TKey extends StringKeyOf<TState> ? Pick<TState, TKey> : { [TLeafKey in LastPathSegment<TKey>]: PathValue<TState, TKey> };
export type ZustandSelectedSlices<TState extends ObjectLike, TKeys extends readonly ZustandSliceKey<TState>[]> = TKeys extends readonly [] ? TState : Simplify<UnionToIntersection<SliceFromKey<TState, TKeys[number]>>>;
const getValueAtPath = (value: ObjectLike, path: string): unknown => {
  return path.split('.').reduce<unknown>((currentValue, pathSegment) => {
    if (currentValue && typeof currentValue === 'object' && pathSegment in currentValue) {
      return (currentValue as Record<string, unknown>)[pathSegment];
    }
    return;
  }, value);
};
const selectZustandSlices = <TState extends ObjectLike,>(state: TState, keys: readonly ZustandSliceKey<TState>[]): Record<string, unknown> => {
  return keys.reduce<Record<string, unknown>>((valuesFromState, key) => {
    const returnValues = {
      ...valuesFromState
    };
    const selectedValue = getValueAtPath(state, key);
    const selectedKey = key.split('.').pop();
    if (selectedKey) {
      returnValues[selectedKey] = selectedValue;
    }
    return returnValues;
  }, {});
};

/**
 * Helper utility for selecting multiple slices from a Zustand store.
 */
export const useZustandSlices = (store, ...t0) => {
  const $ = _c(2);
  const keys = t0;
  let t1;
  if ($[0] !== keys) {
    t1 = state => {
      if (keys.length === 0) {
        return state as ZustandSelectedSlices<TState, TKeys>;
      }
      return selectZustandSlices(state, keys) as ZustandSelectedSlices<TState, TKeys>;
    };
    $[0] = keys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selector = useShallow(t1);
  return useStore(store, selector);
};